import type { Card, Filters, Theme } from '../types.js';

export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
export type ChartRange = '1W' | '1M' | '1Y' | '5Y';
export type ChartPoint = { observedAt: string; price: number };
export type ReturnTone = 'positive' | 'negative' | 'neutral' | 'muted';

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function compareNullable(a: number | null, b: number | null, descending: boolean): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return descending ? b - a : a - b;
}

export function loadWatchlist(storage: StorageLike): string[] {
  try {
    const raw = storage.getItem('tcg-watchlist');
    if (!raw) return [];
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? [...new Set(value.filter((item): item is string => typeof item === 'string'))] : [];
  } catch {
    return [];
  }
}

export function saveWatchlist(storage: StorageLike, watchlist: Set<string>): void {
  try {
    storage.setItem('tcg-watchlist', JSON.stringify([...watchlist]));
  } catch {
    // Storage can be unavailable in private/locked-down browser contexts.
  }
}

export function loadTheme(storage: StorageLike): Theme {
  try {
    const value = storage.getItem('theme');
    return value === 'light' || value === 'dark' ? value : 'dark';
  } catch {
    return 'dark';
  }
}

export function saveTheme(storage: StorageLike, theme: Theme): void {
  try {
    storage.setItem('theme', theme);
  } catch {
    // The selected theme still applies for the current page lifetime.
  }
}

export function filterCards(cards: Card[], filters: Filters, watchlist: Set<string>) {
  const gap = filters.game === 'onepiece' && filters.language === 'ja'
    ? 'Japanese One Piece is unavailable in the current free feed; English cards are not substituted.'
    : null;
  const query = filters.query.toLocaleLowerCase();
  const minimum = filters.minPrice === '' ? null : Number(filters.minPrice);
  return {
    gap,
    items: cards.filter(card => (
      (!filters.game || card.game === filters.game)
      && (!filters.language || card.language === filters.language)
      && (!filters.set || card.setName === filters.set)
      && (!filters.finish || card.finish === filters.finish)
      && (!query || `${card.name} ${card.collectorNumber} ${card.setName}`.toLocaleLowerCase().includes(query))
      && (minimum === null || (Number.isFinite(minimum) && card.price !== null && card.price >= minimum))
      && (!filters.watchedOnly || watchlist.has(card.id))
    )),
  };
}

export function reconcileFilters(filters: Filters, cards: Card[]): Filters {
  const inScope = cards.filter(card => (
    (!filters.game || card.game === filters.game)
    && (!filters.language || card.language === filters.language)
  ));
  const sets = new Set(inScope.map(card => card.setName));
  const finishes = new Set(inScope.map(card => card.finish));
  return {
    ...filters,
    set: filters.set && sets.has(filters.set) ? filters.set : '',
    finish: filters.finish && finishes.has(filters.finish) ? filters.finish : '',
  };
}

export function formatReturn(value: number | null): string {
  if (value === null) return '—';
  const rounded = Number(value.toFixed(1));
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)}%`;
}

export function returnTone(value: number | null): ReturnTone {
  if (value === null) return 'muted';
  const rounded = Number(value.toFixed(1));
  if (rounded === 0) return 'neutral';
  return rounded > 0 ? 'positive' : 'negative';
}

export function formatUsd(value: number): string {
  return usdFormatter.format(value);
}

export function formatProviderTimestamp(value: string): string {
  const instant = new Date(value);
  if (!Number.isFinite(instant.valueOf())) return 'unavailable';
  return `${instant.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

export async function mapWithConcurrency<T, R>(
  values: readonly T[],
  concurrency: number,
  mapper: (value: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (!Number.isInteger(concurrency) || concurrency < 1) throw new Error('Concurrency must be a positive integer');
  const results = new Array<R>(values.length);
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= values.length) return;
      results[index] = await mapper(values[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, values.length) }, () => worker()));
  return results;
}

export function sourceFreshness(providerUpdatedAt: string | null, now = new Date()): 'live' | 'stale' | 'unavailable' {
  if (!providerUpdatedAt) return 'unavailable';
  const instant = new Date(providerUpdatedAt).valueOf();
  if (!Number.isFinite(instant) || instant > now.valueOf() + 5 * 60_000) return 'unavailable';
  return now.valueOf() - instant >= 48 * 60 * 60 * 1000 ? 'stale' : 'live';
}

function subtractCalendarYears(end: Date, years: number): Date {
  const start = new Date(end);
  const month = start.getUTCMonth();
  const day = start.getUTCDate();
  start.setUTCDate(1);
  start.setUTCFullYear(start.getUTCFullYear() - years);
  start.setUTCMonth(month);
  const lastDay = new Date(Date.UTC(start.getUTCFullYear(), month + 1, 0)).getUTCDate();
  start.setUTCDate(Math.min(day, lastDay));
  return start;
}

export function chartRangeStart(end: Date, range: ChartRange): Date {
  const target = range === '1Y'
    ? subtractCalendarYears(end, 1)
    : range === '5Y' ? subtractCalendarYears(end, 5) : new Date(end);
  if (range === '1W' || range === '1M') {
    target.setUTCDate(target.getUTCDate() - (range === '1W' ? 7 : 30));
  }
  target.setUTCHours(0, 0, 0, 0);
  return target;
}

function chartPointTime(point: ChartPoint): number {
  return new Date(point.observedAt.length === 10 ? `${point.observedAt}T00:00:00Z` : point.observedAt).valueOf();
}

export function filterChartPoints(points: ChartPoint[], range: ChartRange): ChartPoint[] {
  const valid = points
    .filter(point => Number.isFinite(chartPointTime(point)) && Number.isFinite(point.price) && point.price >= 0)
    .sort((left, right) => chartPointTime(left) - chartPointTime(right));
  if (valid.length === 0) return [];
  const end = new Date(chartPointTime(valid.at(-1)!));
  const start = chartRangeStart(end, range).valueOf();
  return valid.filter(point => {
    const time = chartPointTime(point);
    return time >= start && time <= end.valueOf();
  });
}

export function chartPriceDomain(prices: number[]): { min: number; max: number } {
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min !== max) return { min, max };
  if (min === 0) return { min: 0, max: 0.01 };
  const padding = Math.max(0.01, Math.round(min * 0.02 * 100) / 100);
  return {
    min: Math.max(0, Math.round((min - padding) * 100) / 100),
    max: Math.round((max + padding) * 100) / 100,
  };
}
